import { useEffect, useState, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ChevronRight, ChevronLeft, Server, Loader2, CheckCircle, AlertTriangle, Clock, FileWarning, Search, Calendar, Filter, XCircle, Activity } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import { ModeToggle } from '../components/mode-toggle';
import { apiClient, IncidentSummary } from '../api/client';

function formatTimeAgo(timestamp: number): string {
  const now = Date.now() / 1000;
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60);
  const hours = Math.floor(diff / 3600);
  const days = Math.floor(diff / 86400);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
}

function formatDateTime(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short'
  });
}

function getSeverityBadgeVariant(severity: string): 'default' | 'destructive' | 'outline' | 'secondary' {
  switch (severity) {
    case 'critical':
    case 'high':
      return 'destructive';
    case 'medium':
      return 'default';
    default:
      return 'secondary';
  }
}

const PAGE_SIZE = 10;

export default function ClusterIncidentGroup() {
  const { clusterId } = useParams<{ clusterId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState<IncidentSummary[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [uniqueWorkloadCount, setUniqueWorkloadCount] = useState(0);
  const [aggregateStats, setAggregateStats] = useState({ investigating: 0, analyzed: 0, resolved: 0 });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageTokenHistory, setPageTokenHistory] = useState<string[]>([]);
  const [showResolveAll, setShowResolveAll] = useState(false);
  const [resolvingAll, setResolvingAll] = useState(false);
  const clusterName = (location.state as any)?.clusterName || 'Unknown Cluster';

  // Filters — initialize from navigation state (carried from incidents list page)
  const [hideTransient, setHideTransient] = useState<boolean>((location.state as any)?.hideTransient ?? true);
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>(() => {
    const nav = (location.state as any)?.dateRange;
    if (nav?.from && nav?.to) {
      return { from: new Date(nav.from), to: new Date(nav.to) };
    }
    const lastDay = new Date();
    lastDay.setDate(lastDay.getDate() - 1);
    lastDay.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return { from: lastDay, to: today };
  });
  const [selectedStatus, setSelectedStatus] = useState<string>((location.state as any)?.selectedStatus || 'all');
  const [searchQuery, setSearchQuery] = useState<string>((location.state as any)?.searchQuery || '');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>((location.state as any)?.searchQuery || '');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 800);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchPage = useCallback(async (pageToken?: string) => {
    if (!clusterId) return;
    setLoading(true);
    const startTime = Math.floor(dateRange.from.getTime() / 1000);
    const endTime = Math.floor(dateRange.to.getTime() / 1000);
    try {
      const response = await apiClient.listClusterSchedulingIncidents(
        clusterId, hideTransient, PAGE_SIZE, pageToken,
        startTime, endTime,
        selectedStatus === 'all' ? undefined : selectedStatus,
        debouncedSearchQuery || undefined,
      );
      setIncidents(response.incidents || []);
      setTotalCount(response.total_count || 0);
      setUniqueWorkloadCount(response.unique_workload_count || response.total_count || 0);
      setAggregateStats({
        investigating: response.investigating_count || 0,
        analyzed: response.resolved_count || 0,
        resolved: response.resolved_confirmed_count || 0,
      });
    } catch (err) {
      console.error('Failed to fetch cluster incidents:', err);
    } finally {
      setLoading(false);
    }
  }, [clusterId, hideTransient, dateRange, selectedStatus, debouncedSearchQuery]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handleNextPage = () => {
    const nextToken = String((currentPage) * PAGE_SIZE);
    setPageTokenHistory(prev => [...prev, nextToken]);
    setCurrentPage(p => p + 1);
    fetchPage(nextToken);
  };

  const handlePrevPage = () => {
    const newHistory = [...pageTokenHistory];
    newHistory.pop();
    setPageTokenHistory(newHistory);
    setCurrentPage(p => p - 1);
    fetchPage(newHistory.length > 0 ? newHistory[newHistory.length - 1] : undefined);
  };

  const handleResolveAll = async () => {
    if (!clusterId) return;
    setResolvingAll(true);
    try {
      await apiClient.resolveAllClusterSchedulingIncidents(clusterId, hideTransient);
      setShowResolveAll(false);
      fetchPage();
    } catch (err) {
      console.error('Failed to resolve all incidents:', err);
    } finally {
      setResolvingAll(false);
    }
  };

  const investigatingCount = aggregateStats.investigating;
  const resolvedCount = aggregateStats.analyzed;
  const resolvedConfirmedCount = aggregateStats.resolved;
  const resolvableCount = totalCount - resolvedConfirmedCount - investigatingCount;
  const hasResolvable = resolvableCount > 0;

  return (
    <div className="space-y-6 overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <AlertTriangle className="h-6 w-6 text-orange-500" />
            <h1 className="text-2xl font-bold tracking-tight">Cluster Resource Exhaustion</h1>
            <Badge variant="destructive" className="self-center mt-1">{uniqueWorkloadCount} workloads affected</Badge>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
            <Server className="h-4 w-4" />
            <span>{clusterName}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {hasResolvable && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowResolveAll(true)}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Resolve All
            </Button>
          )}
          <ModeToggle />
        </div>
      </div>

      <div className="space-y-6">
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Affected Workloads</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{uniqueWorkloadCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Investigating</CardTitle>
              <Loader2 className={`h-4 w-4 text-blue-500 ${investigatingCount > 0 ? 'animate-spin' : ''}`} />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{investigatingCount}</div>
              <p className="text-xs text-muted-foreground">AI analysis in progress</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Analysis Complete</CardTitle>
              <FileWarning className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">{resolvedCount}</div>
              <p className="text-xs text-muted-foreground">Ready to apply</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Resolved</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{resolvedConfirmedCount}</div>
              <p className="text-xs text-muted-foreground">Incident closed</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by type, resource..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                    setPageTokenHistory([]);
                  }}
                  className="pl-8"
                />
              </div>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full sm:w-[280px] justify-start text-left font-normal")}>
                    <Calendar className="mr-2 h-4 w-4" />
                    {format(dateRange.from, 'MMM d, yyyy')} - {format(dateRange.to, 'MMM d, yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange.from}
                    selected={{ from: dateRange.from, to: dateRange.to }}
                    onSelect={(range: any) => {
                      if (range?.from && range?.to) {
                        const from = new Date(range.from);
                        from.setHours(0, 0, 0, 0);
                        const to = new Date(range.to);
                        to.setHours(23, 59, 59, 999);
                        setDateRange({ from, to });
                        setCurrentPage(1);
                        setPageTokenHistory([]);
                      }
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>

              <Select value={selectedStatus} onValueChange={(val) => { setSelectedStatus(val); setCurrentPage(1); setPageTokenHistory([]); }}>
                <SelectTrigger className="w-[200px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="investigating">Investigating</SelectItem>
                  <SelectItem value="resolved">Analysis Complete</SelectItem>
                  <SelectItem value="resolved_confirmed">Resolved</SelectItem>
                </SelectContent>
              </Select>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant={hideTransient ? "secondary" : "outline"}
                      onClick={() => { setHideTransient(!hideTransient); setCurrentPage(1); setPageTokenHistory([]); }}
                      className="whitespace-nowrap"
                    >
                      {hideTransient ? <XCircle className="mr-2 h-4 w-4" /> : <Activity className="mr-2 h-4 w-4" />}
                      {hideTransient ? 'Hiding Transient' : 'Show All'}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{hideTransient ? 'Click to show transient incidents' : 'Click to hide transient incidents'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Affected Workloads</CardTitle>
            <CardDescription>
              Kubernetes incidents detected and analyzed by AI agents
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : incidents.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
                <h3 className="text-lg font-semibold mb-2">No workload incidents found</h3>
                <p className="text-sm text-muted-foreground">
                  The affected workloads may have recovered or been resolved.
                </p>
              </div>
            ) : (
              <>
                <div className="border rounded-md overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Incident</TableHead>
                        <TableHead>Affected Resource</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Detected</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {incidents.map((incident) => (
                        <TableRow
                          key={incident.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/incidents/${incident.id}`)}
                        >
                          <TableCell>
                            <div className="space-y-1">
                              <div className="font-medium">{incident.type}</div>
                              <div className="flex items-center gap-2">
                                <Badge variant={getSeverityBadgeVariant(incident.severity)}>
                                  {incident.severity}
                                </Badge>
                                {incident.has_rca_result && incident.yaml_fix_count > 0 && (
                                  <Badge variant="outline" className="text-xs">
                                    <FileWarning className="h-3 w-3 mr-1" />
                                    {incident.yaml_fix_count} YAML fix{incident.yaml_fix_count !== 1 ? 'es' : ''}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Server className="h-4 w-4 text-muted-foreground" />
                              <div className="min-w-0">
                                <div className="font-mono text-sm truncate">{incident.primary_resource}</div>
                                <div className="text-xs text-muted-foreground">{incident.primary_resource_kind}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {incident.status === 'investigating' || incident.status === 'analyzing_code' ? (
                                <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                              ) : incident.status === 'resolved_confirmed' ? (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              ) : incident.status === 'remediation_applied' ? (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              ) : (
                                <FileWarning className="h-4 w-4 text-amber-500" />
                              )}
                              <span className="text-sm">
                                {incident.status === 'investigating' || incident.status === 'analyzing_code'
                                  ? 'Investigating'
                                  : incident.status === 'resolved_confirmed'
                                    ? 'Resolved'
                                    : incident.status === 'remediation_applied'
                                      ? 'Remediation Applied'
                                      : 'Analysis Complete'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              <span title={formatDateTime(incident.detected_at)}>
                                {formatTimeAgo(incident.detected_at)}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/incidents/${incident.id}`);
                            }}>
                              View Details
                              <ChevronRight className="ml-2 h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-2 py-4">
                    <div className="text-sm text-muted-foreground">
                      Showing {((currentPage - 1) * PAGE_SIZE) + 1} to {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} incidents
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePrevPage}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={showResolveAll} onOpenChange={setShowResolveAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resolve All Incidents</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark {resolvableCount} workload incident{resolvableCount !== 1 ? 's' : ''} with completed analysis as resolved.
              {investigatingCount > 0 && ` ${investigatingCount} incident${investigatingCount !== 1 ? 's' : ''} still being investigated will not be affected.`}
              {resolvedConfirmedCount > 0 && ` ${resolvedConfirmedCount} already resolved.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resolvingAll}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResolveAll} disabled={resolvingAll}>
              {resolvingAll ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Resolving...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Resolve All
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
